import type { Criticality, DeliveryChannel, DeliveryStatus, HealthState, ProblemSeverity } from "../types";

export const HEALTH_LABELS: Record<HealthState, string> = {
  operational: "Operativo",
  degraded: "Con problemas",
  outage: "Fuera de servicio",
  stale: "Sin datos recientes",
  maintenance: "En mantenimiento",
};

export const SEVERITY_LABELS: Record<ProblemSeverity, string> = {
  disaster: "Desastre",
  high: "Alta",
  average: "Media",
  warning: "Advertencia",
  information: "Información",
  not_classified: "Sin clasificar",
};

export const CRITICALITY_LABELS: Record<Criticality, string> = {
  critical: "Crítico",
  important: "Importante",
  standard: "Estándar",
};

export const CHANNEL_LABELS: Record<DeliveryChannel, string> = {
  email: "Correo",
  sms: "SMS",
  whatsapp: "WhatsApp",
  web_push: "Push web",
};

export const DELIVERY_LABELS: Record<DeliveryStatus, string> = {
  pending: "Pendiente",
  processing: "Procesando",
  queued: "En cola",
  sent: "Enviado",
  delivered: "Entregado",
  read: "Leído",
  failed: "Falló",
  undelivered: "No entregado",
  cancelled: "Cancelado",
};

const fullDateTime = new Intl.DateTimeFormat("es-AR", {
  dateStyle: "short",
  timeStyle: "medium",
  timeZone: "America/Argentina/Buenos_Aires",
});

const shortTime = new Intl.DateTimeFormat("es-AR", {
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "America/Argentina/Buenos_Aires",
});

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return "Sin registro";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Fecha inválida" : fullDateTime.format(date);
}

export function formatTime(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : shortTime.format(date);
}

export function relativeTime(value: string | null | undefined, now = Date.now()): string {
  if (!value) return "Nunca";
  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) return "Fecha inválida";
  const seconds = Math.round((timestamp - now) / 1000);
  const absolute = Math.abs(seconds);
  const formatter = new Intl.RelativeTimeFormat("es", { numeric: "auto" });
  if (absolute < 60) return formatter.format(seconds, "second");
  const minutes = Math.round(seconds / 60);
  if (Math.abs(minutes) < 60) return formatter.format(minutes, "minute");
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return formatter.format(hours, "hour");
  return formatter.format(Math.round(hours / 24), "day");
}

export function durationBetween(startedAt: string, endedAt: string): string {
  const start = new Date(startedAt).getTime();
  const end = new Date(endedAt).getTime();
  if (Number.isNaN(start) || Number.isNaN(end)) return "Sin registro";
  return formatDuration(Math.max(0, end - start));
}

export function durationSince(value: string): string {
  return durationBetween(value, new Date().toISOString());
}

function formatDuration(milliseconds: number): string {
  const minutes = Math.floor(milliseconds / 60_000);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ${minutes % 60} min`;
  return `${Math.floor(hours / 24)} d ${hours % 24} h`;
}

export function percentage(part: number, total: number): number {
  return total <= 0 ? 0 : Math.round((part / total) * 100);
}

export function initials(value: string): string {
  return value.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase() ?? "").join("");
}
